/**
 * Offer checker — TanStack Query hooks.
 *
 * Wraps the POST /api/v1/offer-checker/run mutation plus the optional
 * /campaigns dropdown query. The mutation invalidates the "runs" key so
 * any surrounding history list can refresh itself without extra calls.
 */

import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	listCampaigns,
	offerCheckerQueryKeys,
	runCheck,
	type CampaignOption,
	type OfferCheckRunView,
	type RunCheckBody,
} from "./api";

/** Mutation — run a single offer diagnostic check. */
export function useOfferCheckMutation(): UseMutationResult<
	OfferCheckRunView,
	Error,
	RunCheckBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: offerCheckerQueryKeys.mutation("run"),
		mutationFn: runCheck,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: offerCheckerQueryKeys.runs() });
		},
	});
}

/** Optional dropdown query — never throws; returns [] on failure. */
export function useCampaignsList(): UseQueryResult<CampaignOption[], Error> {
	return useQuery({
		queryKey: offerCheckerQueryKeys.campaigns(),
		queryFn: listCampaigns,
		staleTime: 60_000,
		retry: false,
	});
}
